import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { ActionResult, EventItem, EventStatus, LeagueFilter, Lookups, PagedFilter } from "@model";
import { Observable, BehaviorSubject } from "rxjs";
import { map, tap } from "rxjs/operators";
import { League } from "@model";
import { LoaderService } from "./loader.service";
import dayjs from "dayjs";
import { EnvironmentService } from "./environment.service";

@Injectable({ providedIn: 'root' })
export class LeagueService {
  private static _currentLeague = new BehaviorSubject<League | undefined>(undefined);

  get currentLeague(): BehaviorSubject<League | undefined> {
    return LeagueService._currentLeague;
  }

  constructor(
    private http: HttpClient,
    private loaderService: LoaderService,
    private environmentService: EnvironmentService
  ) {}

  setCurrentLeague(league: League | undefined): void {
    this.currentLeague.next(league);
  }

  getFilteredLeagueStatuses(): Observable<Lookups[] | []> {
    return this.http.get<ActionResult<Lookups[]>>(`${this.environmentService.getApiUrl()}/api/public/lookups/leaguestatusesfiltered`).pipe(
      map(value => value?.result?.length ? value.result : [])
    );
  }

  findLeagues(): Observable<League[]> {
    return this.http.get<ActionResult<League[]>>(`${this.environmentService.getApiUrl()}/api/leagues/fetchall`).pipe(
      map(actionResult => actionResult.resultCode !== -1 && actionResult.result ? actionResult.result : [])
    );
  }

  findPublicLeagues(filter: PagedFilter<LeagueFilter>): Observable<League[]> {
    return this.http.post<ActionResult<League[]>>(`${this.environmentService.getApiUrl()}/api/public/leagues`, filter).pipe(
      map(actionResult => actionResult.resultCode !== -1 && actionResult.result ? actionResult.result : [])
    );
  }

  fetchLeague(leagueId: number): Observable<League | undefined> {
    return this.http.post<ActionResult<League>>(`${this.environmentService.getApiUrl()}/api/leagues/fetchbyid`, leagueId).pipe(
      map(actionResult => {
        const league = actionResult.resultCode !== -1 && actionResult.result ? actionResult.result : undefined;
        this.currentLeague.next(league);
        return league;
      })
    );
  }

  fetchPublicLeague(leagueId: number): Observable<League | undefined> {
    return this.http.post<ActionResult<League>>(`${this.environmentService.getApiUrl()}/api/public/leagues/fetchbyid`, leagueId).pipe(
      map(actionResult => {
        const league = actionResult.resultCode !== -1 && actionResult.result ? actionResult.result : undefined;
        if (league) this.sortEvents(league);
        this.currentLeague.next(league);
        return league;
      })
    );
  }

  private sortEvents(league: League) {
    if (league?.events?.length) {
      const finishedEvents = league.events.filter(event => event.status === EventStatus.FINISHED);
      const activeEvents = league.events.filter(event => event.status === EventStatus.ACTIVE);
      finishedEvents.sort((a, b) => dayjs(a.start!).toDate().getTime() - dayjs(b.start!).toDate().getTime());
      activeEvents.sort((a, b) => dayjs(a.start!).toDate().getTime() - dayjs(b.start!).toDate().getTime());
      league.events = [...activeEvents, ...finishedEvents];
    }
  }

  updateLeague(league: League, onSuccess: (league: League) => void, onError: () => void): void {
    this.http.post<ActionResult<boolean | number>>(
      `${this.environmentService.getApiUrl()}${league.id ? "/api/leagues/update" : "/api/leagues/add"}`, league
    ).subscribe(value => {
      if (value?.result) {
        if (typeof value.result === "boolean") {
          this.setCurrentLeague(league);
        } else {
          league.id = value.result;
          this.setCurrentLeague(league);
        }
        onSuccess(league);
      } else {
        onError();
      }
    });
  }

  deleteLeague(league: League, onSuccess: () => void, onError: () => void): void {
    this.http.post<ActionResult<boolean>>(
      `${this.environmentService.getApiUrl()}/api/leagues/delete`, league.id
    ).subscribe(actionResult => {
      actionResult.success ? onSuccess() : onError();
    });
  }

  availableEventsForLeague(leagueId?: number): Observable<EventItem[]> {
    this.loaderService.startLoading();
    return this.http.post<ActionResult<EventItem[]>>(
      `${this.environmentService.getApiUrl()}/api/leagues/availableevents/${leagueId}`, undefined
    ).pipe(
      tap(() => this.loaderService.dismissLoading()),
      map(value => value?.result?.length ? value.result : [])
    );
  }

  addEventsToLeague(league: League, onSuccess: () => void, onError: () => void): void {
    this.http.post<ActionResult<boolean>>(
      `${this.environmentService.getApiUrl()}/api/leagues/addleagueevents`, league
    ).subscribe(actionResult => {
      actionResult.success ? onSuccess() : onError();
    });
  }

  removeEventFromLeague(eventId: number, leagueId: number, onSuccess: () => void, onError: () => void): void {
    this.http.post<ActionResult<boolean>>(
      `${this.environmentService.getApiUrl()}/api/leagues/removeevent/eventId/${eventId}/leagueId/${leagueId}`, undefined
    ).subscribe(actionResult => {
      actionResult.success ? onSuccess() : onError();
    });
  }
}
